import { FormEvent, useEffect, useState } from 'react';
import {
  Edit3,
  FlaskConical,
  Loader2,
  Plus,
  Power,
  Save,
  X,
} from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import FormModal from '../../../components/Modals/FormModal';
import useAlerts from '../../../hooks/useAlerts';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import useConfirm from '../../../hooks/useConfirmModal';
import RestaurantRawMaterialIdentifierService from '../../../services/restaurant/RestaurantRawMaterialIdentifierService';
import {
  RawMaterialSupplierOption,
  RestaurantRawMaterialDTO,
} from '../../../types/restaurant/RestaurantRawMaterialTypes';
import {
  RawMaterialIdentifierType,
  RestaurantRawMaterialIdentifierDTO,
  RestaurantRawMaterialIdentifierForm,
} from '../../../types/restaurant/RestaurantRawMaterialIdentifierTypes';

interface Props {
  isOpen: boolean;
  branchId: number;
  rawMaterial: RestaurantRawMaterialDTO | null;
  suppliers: RawMaterialSupplierOption[];
  onClose: () => void;
}

const emptyForm: RestaurantRawMaterialIdentifierForm = {
  supplierId: '',
  type: 'NAME',
  value: '',
};

const typeLabels: Record<RawMaterialIdentifierType, string> = {
  NAME: 'Nombre de proveedor',
  CODE: 'Código alternativo',
  BARCODE: 'Código de barras',
};

const RawMaterialIdentifiersModal = ({
  isOpen,
  branchId,
  rawMaterial,
  suppliers,
  onClose,
}: Props) => {
  // =====================================================
  // 🔹 Estados
  // =====================================================

  const [items, setItems] = useState<
    RestaurantRawMaterialIdentifierDTO[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] =
    useState<RestaurantRawMaterialIdentifierForm>(emptyForm);

  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();

  // =====================================================
  // 🔹 Cargar identificadores
  // =====================================================

  const load = async () => {
    if (!rawMaterial) return;

    setLoading(true);

    try {
      const response =
        await RestaurantRawMaterialIdentifierService.list(
          rawMaterial.id
        );

      setItems(response.data ?? []);
    } catch (error) {
      handle(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && rawMaterial) {
      load();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rawMaterial?.id]);

  // =====================================================
  // 🔹 Formulario
  // =====================================================

  const openCreateForm = () => {
    setForm({ ...emptyForm });
    setFormOpen(true);
  };

  const openEditForm = (
    item: RestaurantRawMaterialIdentifierDTO
  ) => {
    setForm({
      id: item.id,
      supplierId: item.supplierId ?? '',
      type: item.type,
      value: item.value,
    });

    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;

    setFormOpen(false);
    setForm({ ...emptyForm });
  };

  const saveIdentifier = async () => {
    if (!rawMaterial || !form.value.trim() || saving) return;

    setSaving(true);

    try {
      const payload = {
        ...form,
        value: form.value.trim(),
      };

      const response = payload.id
        ? await RestaurantRawMaterialIdentifierService.update(
            payload.id,
            payload
          )
        : await RestaurantRawMaterialIdentifierService.create(
            rawMaterial.id,
            payload
          );

      show(
        response.type ?? 'success',
        response.title ?? 'Identificador guardado',
        response.message ??
          'El identificador se guardó correctamente.'
      );

      setFormOpen(false);
      setForm({ ...emptyForm });

      await load();
    } catch (error) {
      handle(error);
    } finally {
      setSaving(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveIdentifier();
  };

  // =====================================================
  // 🔹 Activar / desactivar
  // =====================================================

  const toggle = (
    item: RestaurantRawMaterialIdentifierDTO
  ) => {
    const execute = async () => {
      setTogglingId(item.id);

      try {
        const response =
          await RestaurantRawMaterialIdentifierService.updateActive(
            item.id,
            !item.active
          );

        show(
          response.type ?? 'success',
          response.title ?? 'Estado actualizado',
          response.message ??
            'El identificador fue actualizado.'
        );

        await load();
      } catch (error) {
        handle(error);
      } finally {
        setTogglingId(null);
      }
    };

    if (!item.active) {
      execute();
      return;
    }

    confirm({
      title: 'Desactivar identificador',
      message: `¿Desea desactivar “${item.value}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  // =====================================================
  // 🔹 Probar resolución
  // =====================================================

  const test = async (
    item: RestaurantRawMaterialIdentifierDTO
  ) => {
    setTestingId(item.id);

    try {
      const response =
        await RestaurantRawMaterialIdentifierService.resolve({
          branchId,
          supplierId: item.supplierId,
          type: item.type,
          value: item.value,
        });

      const match = response.data;

      show(
        response.type ?? 'success',
        response.title ?? 'Identificador resuelto',
        response.message ??
          (match
            ? `Coincidencia: ${match.code} · ${match.name}`
            : 'El identificador se resolvió correctamente.')
      );
    } catch (error) {
      handle(
        error,
        'No se encontró una materia prima para este identificador.'
      );
    } finally {
      setTestingId(null);
    }
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Nombres e identificadores · ${
          rawMaterial?.name ?? ''
        }`}
        maxWidth="max-w-6xl"
      >
        {/* =====================================================
            🔹 Encabezado
        ===================================================== */}

        <div
          className="
            mb-5

            flex
            flex-col
            gap-3

            sm:flex-row
            sm:items-center
            sm:justify-between
          "
        >
          <p className="max-w-3xl text-sm text-bodydark2">
            Registre nombres y códigos globales o específicos de
            un proveedor. La resolución siempre la realiza el
            servidor.
          </p>

          <ActionButton
            label="Nuevo identificador"
            icon={<Plus className="h-4 w-4" />}
            variant="primary"
            size="md"
            fullWidth={false}
            onClick={openCreateForm}
            className="shrink-0 whitespace-nowrap"
          />
        </div>

        {/* =====================================================
            🔹 Cargando
        ===================================================== */}

        {loading ? (
          <div
            className="
              flex
              min-h-[240px]
              items-center
              justify-center

              rounded-xl

              border
              border-stroke

              bg-gray-2/30

              dark:border-strokedark
              dark:bg-boxdark-2/30
            "
          >
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          /* =====================================================
              🔹 Estado vacío
          ===================================================== */

          <div
            className="
              flex
              min-h-[240px]
              flex-col
              items-center
              justify-center

              rounded-xl

              border
              border-dashed
              border-stroke

              bg-gray-2/30

              px-5

              text-center

              dark:border-strokedark
              dark:bg-boxdark-2/30
            "
          >
            <span
              className="
                rounded-xl

                border
                border-primary/15

                bg-primary/10

                p-3

                text-primary

                dark:border-primary/25
                dark:bg-primary/15
              "
            >
              <FlaskConical className="h-7 w-7" />
            </span>

            <p
              className="
                mt-4
                font-medium
                text-black

                dark:text-white
              "
            >
              Aún no hay identificadores
            </p>

            <p className="mt-1 text-sm text-bodydark2">
              Puede agregar tantos como necesite.
            </p>
          </div>
        ) : (
          /* =====================================================
              🔹 Tabla
          ===================================================== */

          <div
            className="
              overflow-hidden

              rounded-xl

              border
              border-stroke

              bg-white

              shadow-card-2

              dark:border-strokedark
              dark:bg-boxdark
              dark:shadow-none
            "
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-left text-sm">
                <thead
                  className="
                    bg-gray-2

                    text-xs
                    uppercase
                    tracking-wide
                    text-bodydark2

                    dark:bg-meta-4/60
                  "
                >
                  <tr>
                    <th className="px-4 py-3 font-medium">
                      Valor
                    </th>

                    <th className="px-4 py-3 font-medium">
                      Tipo
                    </th>

                    <th className="px-4 py-3 font-medium">
                      Proveedor
                    </th>

                    <th className="px-4 py-3 font-medium">
                      Estado
                    </th>

                    <th className="px-4 py-3 text-right font-medium">
                      Acciones
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item) => {
                    const isTesting = testingId === item.id;
                    const isToggling = togglingId === item.id;

                    return (
                      <tr
                        key={item.id}
                        className={`
                          border-t
                          border-stroke

                          transition-colors

                          hover:bg-gray-2/40

                          dark:border-strokedark
                          dark:hover:bg-meta-4/20

                          ${item.active ? '' : 'opacity-60'}
                        `}
                      >
                        <td className="px-4 py-3">
                          <p
                            className="
                              font-medium
                              text-black

                              dark:text-white
                            "
                          >
                            {item.value}
                          </p>

                          {item.normalizedValue && (
                            <p
                              className="
                                mt-1
                                text-xs
                                text-bodydark2
                              "
                              title="Valor normalizado por el backend"
                            >
                              Normalizado:{' '}
                              {item.normalizedValue}
                            </p>
                          )}
                        </td>

                        <td
                          className="
                            px-4
                            py-3
                            text-black

                            dark:text-white
                          "
                        >
                          {typeLabels[item.type]}
                        </td>

                        <td
                          className="
                            px-4
                            py-3
                            text-black

                            dark:text-white
                          "
                        >
                          {item.supplierName ||
                            'Global · sin proveedor'}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`
                              inline-flex
                              items-center

                              rounded-full

                              px-2.5
                              py-1

                              text-xs
                              font-medium

                              ${
                                item.active
                                  ? `
                                    bg-success/10
                                    text-success

                                    dark:bg-success/15
                                  `
                                  : `
                                    bg-danger/10
                                    text-danger

                                    dark:bg-danger/15
                                  `
                              }
                            `}
                          >
                            {item.active
                              ? 'Activo'
                              : 'Inactivo'}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div
                            className="
                              flex
                              flex-wrap
                              justify-end
                              gap-2
                            "
                          >
                            <ActionButton
                              label={
                                isTesting
                                  ? 'Probando'
                                  : 'Probar'
                              }
                              icon={
                                isTesting ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <FlaskConical className="h-4 w-4" />
                                )
                              }
                              variant="info"
                              size="sm"
                              fullWidth={false}
                              disabled={
                                !item.active ||
                                isTesting ||
                                isToggling
                              }
                              onClick={() => test(item)}
                              className="whitespace-nowrap"
                            />

                            <ActionButton
                              label="Editar"
                              icon={<Edit3 className="h-4 w-4" />}
                              variant="secondary"
                              size="sm"
                              fullWidth={false}
                              disabled={isTesting || isToggling}
                              onClick={() => openEditForm(item)}
                              className="whitespace-nowrap"
                            />

                            <ActionButton
                              label={
                                isToggling
                                  ? 'Actualizando'
                                  : item.active
                                  ? 'Desactivar'
                                  : 'Activar'
                              }
                              icon={
                                isToggling ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Power className="h-4 w-4" />
                                )
                              }
                              variant={
                                item.active
                                  ? 'danger'
                                  : 'success'
                              }
                              size="sm"
                              fullWidth={false}
                              disabled={isTesting || isToggling}
                              onClick={() => toggle(item)}
                              className="whitespace-nowrap"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </FormModal>

      {/* =====================================================
          🔹 Modal crear / editar
      ===================================================== */}

      <FormModal
        isOpen={formOpen}
        onClose={saving ? undefined : closeForm}
        title={
          form.id
            ? 'Editar identificador'
            : 'Nuevo identificador'
        }
        maxWidth="max-w-2xl"
      >
        <form onSubmit={submit}>
          <SelectField
            label="Tipo"
            name="identifierType"
            value={form.type}
            iconKey="tag"
            options={(
              Object.keys(
                typeLabels
              ) as RawMaterialIdentifierType[]
            ).map((value) => ({
              value,
              label: typeLabels[value],
            }))}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                type: event.target
                  .value as RawMaterialIdentifierType,
              }))
            }
          />

          <SelectField
            label="Proveedor (opcional)"
            name="identifierSupplier"
            value={form.supplierId}
            iconKey="supplier"
            placeholder="Global · sin proveedor"
            options={[
              {
                value: 0,
                label: 'Global · sin proveedor',
              },
              ...suppliers.map((supplier) => ({
                value: supplier.id,
                label:
                  supplier.tradeName || supplier.name,
              })),
            ]}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                supplierId:
                  Number(event.target.value) === 0
                    ? ''
                    : Number(event.target.value),
              }))
            }
          />

          <InputField
            label="Valor"
            name="identifierValue"
            type="text"
            value={form.value}
            iconKey="code"
            required
            error={
              !form.value.trim()
                ? 'El valor es obligatorio.'
                : undefined
            }
            placeholder="Ej. CHD-001"
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                value: event.target.value,
              }))
            }
          />

          <div
            className="
              mt-2

              flex
              flex-col-reverse
              gap-3

              border-t
              border-stroke

              pt-5

              dark:border-strokedark

              sm:flex-row
              sm:justify-end
            "
          >
            <ActionButton
              label="Cancelar"
              icon={<X className="h-4 w-4" />}
              variant="secondary"
              size="md"
              fullWidth={false}
              disabled={saving}
              onClick={closeForm}
              className="w-full sm:w-auto"
            />

            <ActionButton
              label={saving ? 'Guardando' : 'Guardar'}
              icon={
                saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )
              }
              variant="primary"
              size="md"
              fullWidth={false}
              disabled={saving || !form.value.trim()}
              onClick={saveIdentifier}
              className="w-full sm:w-auto"
            />
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default RawMaterialIdentifiersModal;